import { useEffect, useRef, useState, FC } from 'react'

import { useSelectedOptionsCount, useFiltersInfo } from './hooks'
import getFilteredOptions from 'services/getFilteredOptions'
import { FilterCollapsable, FilterSelectionList, FilterDropDown } from '.'
import { FILTER_MAX_SORTED_COLLAPSABLES, FILTER_MIN_INPUT_LENGTH } from 'constant'
import { MFilters } from 'types/filterTypes'
import { FilterType } from 'services/types'

import { t } from 'translations'
import { Badge, Input } from 'ui'

type FiltersButtonprops = {
  contentName: MFilters.ContentTypes
  filters?: string[]
}

const FiltersButton: FC<FiltersButtonprops> = ({ filters, contentName }) => {
  const count = useSelectedOptionsCount({ contentName, filters })
  const filterTypes = useFiltersInfo(contentName, filters)
  const [userInput, setUserInput] = useState('')
  const [filteredFilterTypes, setFilteredFilterTypes] = useState<FilterType[]>([])
  const [show, setShow] = useState(false)
  const advancedInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!userInput) {
      setFilteredFilterTypes(filterTypes)
      setShow(false)
      return
    }
    const filteredFilterTypes = filterTypes
      .map(filterType => {
        const filteredOptions = getFilteredOptions({ userInput, type: filterType.type, sortedOptions: filterType.options })
        return { ...filterType, options: filterType.options, filteredOptions }
      })
      .filter(({ filteredOptions }) => filteredOptions.length !== 0)

    // If there are less than 7 collapsable types sorted from the userInput, open them all (limit to 7 arbitrary)
    if (userInput.length >= FILTER_MIN_INPUT_LENGTH || filteredFilterTypes.length <= FILTER_MAX_SORTED_COLLAPSABLES) setShow(true)
    else setShow(false)

    setFilteredFilterTypes(filteredFilterTypes)
  }, [filterTypes, userInput])

  if (!filters?.length) return null

  return (
    <FilterDropDown
      buttonContent={
        <div className="d-flex flex-row justify-content-between align-items-baseline ">
          <div className="d-flex flex-row align-items-baseline">
            <i className={`fas fa-filter ${count ? ' text-primary' : ''} mr-1 `} />
            <div className="d-sm-block d-none"> {t('searchBar.FILTERS')}</div>
          </div>
          <div className="d-flex flex-row align-items-baseline">
            {count ? (
              <Badge variant="primary" className="ml-2">
                {count}
              </Badge>
            ) : null}
            <i className={`fa fa-caret-down ml-2`} />
          </div>
        </div>
      }
      contentName={contentName}
      filters={filters}
      splitButton="clearButton"
      count={count}
      dropDownType="FiltersButton"
    >
      {() => (
        <div className=" overflow-auto p-2" style={{ maxHeight: '400px', width: '390px' }}>
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fa fa-search mr-1" />
              </span>
            </div>
            <Input
              type="text"
              variant="small"
              placeholder={t('searchBar.TYPE_TO_SEARCH')}
              aria-label="Search"
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              autoFocus
              onFocus={() => setUserInput('')}
              ref={advancedInputRef}
            />
          </div>
          {filteredFilterTypes.map((filter, index) => {
            if (!filter.options.length) return null
            return (
              <FilterCollapsable
                title={
                  <>
                    <i className={`fa ${filter.icon} mr-2`}></i>
                    <b>{filter.label} </b>
                  </>
                }
                type={filter.type}
                contentName={contentName}
                noPadding
                clearButton
                key={filter.key}
                show={show}
              >
                {({ isOpen }) => (
                  <FilterSelectionList
                    filter={filter}
                    contentName={contentName}
                    advancedInput={userInput}
                    advancedInputRef={index === 0 ? advancedInputRef : null}
                    isOpen={isOpen}
                  />
                )}
              </FilterCollapsable>
            )
          })}
        </div>
      )}
    </FilterDropDown>
  )
}

export default FiltersButton
